// Core routes - common for all logged in users (RoutesCoreApp)
import React from "react"
import { Navigate, Route } from "react-router-dom"
import {
	LazyAddInvoice,
	LazyAddMessage,
	LazyAddPayment,
	LazyBusinessSettingsPage,
	LazyCase,
	LazyDialog,
	LazyDocumentUploadCard,
	LazyGlobalSettingsPage,
	LazyNotificationCenter,
	LazyHelpCenter,
	LazyMessage,
	LazyMessages,
	LazyPersonalSettingsPage,
	LazyProductSettingsPage,
	LazySSOTestComplete,
	LazyUploadDocumentsBanner,
} from "./heavyRouteChunks"
import ProfileRedirect from "components/ProfileRedirect/ProfileRedirect"
import { SandboxAccessGate } from "components/SandboxAccess"
import { areSandboxEntryPointsEnabled, SANDBOX_FEATURE } from "helpers/sandboxAccess"

export const BILLING_HISTORY_PATH = "/product-settings/billing-history"
export const PRODUCT_SETTINGS_APPLICATIONS_PATH = "/product-settings/applications"
export const PRODUCT_SETTINGS_PLANS_AND_USAGE_PATH = "/product-settings/plans-and-usage"
export const PRODUCT_SETTINGS_PAYMENTS_PATH = "/product-settings/payments"
export const PRODUCT_SETTINGS_SANDBOX_PATH = "/product-settings/sandbox"

export function isBillingSidebarActive(pathname) {
	return (
		pathname.startsWith(BILLING_HISTORY_PATH) ||
		pathname.startsWith(PRODUCT_SETTINGS_PLANS_AND_USAGE_PATH) ||
		pathname.startsWith(PRODUCT_SETTINGS_PAYMENTS_PATH) ||
		pathname.startsWith(PRODUCT_SETTINGS_SANDBOX_PATH) ||
		pathname.startsWith("/billing")
	)
}

/**
 * Get core routes (RoutesCoreApp equivalent)
 * These routes are available for all logged in users across all projects
 * @returns {JSX.Element} React fragment with routes
 */
export const getCoreRoutes = () => {
	return (
		<>
			<Route
				path='/profile'
				element={<ProfileRedirect />}
			/>
			<Route
				path='/billing'
				element={<Navigate to={BILLING_HISTORY_PATH} replace />}
			/>
			<Route
				path='/billing/add-payment'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.PAYMENT_MUTATIONS}>
						<LazyAddPayment />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/billing/add-invoice'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.PAYMENT_MUTATIONS}>
						<LazyAddInvoice />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/messages'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.MESSAGES}>
						<LazyMessages />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/messages/add'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.MESSAGES}>
						<LazyAddMessage />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/messages/:messageId'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.MESSAGES}>
						<LazyMessage />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/upload-documents/:messageId'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.DOCUMENTS}>
						<LazyUploadDocumentsBanner />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/upload-documents/:messageId/upload'
				element={
					<SandboxAccessGate feature={SANDBOX_FEATURE.DOCUMENTS}>
						<LazyDocumentUploadCard />
					</SandboxAccessGate>
				}
			/>
			<Route
				path='/help'
				element={<LazyHelpCenter />}
			/>
			<Route
				path='/help/messenger/:dialogId'
				element={<LazyDialog />}
			/>
			<Route
				path='/help/messenger/case'
				element={<LazyCase />}
			/>
			<Route
				path='/profile/sso_complete'
				element={<LazySSOTestComplete />}
			/>
			<Route
				path='/personal-settings'
				element={<LazyPersonalSettingsPage />}
			/>
			<Route
				path='/business-settings'
				element={<LazyBusinessSettingsPage />}
			/>
			<Route
				path='/global-settings'
				element={<LazyGlobalSettingsPage />}
			/>
			<Route
				path='/notification-center'
				element={<LazyNotificationCenter />}
			/>
			<Route
				path={PRODUCT_SETTINGS_APPLICATIONS_PATH}
				element={<LazyProductSettingsPage />}
			/>
			<Route
				path={BILLING_HISTORY_PATH}
				element={<LazyProductSettingsPage />}
			/>
			<Route
				path={PRODUCT_SETTINGS_PLANS_AND_USAGE_PATH}
				element={<LazyProductSettingsPage />}
			/>
			<Route
				path={PRODUCT_SETTINGS_PAYMENTS_PATH}
				element={<LazyProductSettingsPage />}
			/>
			<Route
				path={PRODUCT_SETTINGS_SANDBOX_PATH}
				element={
					areSandboxEntryPointsEnabled() ? (
						<LazyProductSettingsPage />
					) : (
						<Navigate to="/global-settings" replace />
					)
				}
			/>
		</>
	)
}
